import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { useToastStore } from '../stores/toast';

export default function Profile() {
  const { user, login, accessToken, refreshToken } = useAuthStore();
  const showToast = useToastStore((state) => state.showToast);
  const [name, setName] = useState(user?.name || '');

  // 비밀번호 변경
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 이메일 변경
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  // 사용자 지정 ID 변경
  const [loginId, setLoginId] = useState('');
  const [loginIdPassword, setLoginIdPassword] = useState('');

  // 휴대폰 번호 변경 관련 상태
  const [isPhoneChangeMode, setIsPhoneChangeMode] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);

  // 본인 정보 조회
  const { data: meData, refetch: refetchMe } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => adminApi.getMe(),
  });

  const phone = meData?.data?.phone || '';

  useEffect(() => {
    if (meData?.data?.name) {
      setName(meData.data.name);
    }
  }, [meData?.data?.name]);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string }) =>
      adminApi.updateProfile(data),
    onSuccess: (result) => {
      if (result.success && result.data) {
        login(
          {
            id: result.data.id,
            email: result.data.email,
            name: result.data.name,
            isSuperAdmin: result.data.isSuperAdmin,
          },
          accessToken!,
          refreshToken!
        );
        refetchMe();
        showToast('이름이 변경되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '업데이트에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('업데이트 중 오류가 발생했습니다.', 'error');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      adminApi.changeMyPassword(data),
    onSuccess: (result) => {
      if (result.success) {
        showToast('비밀번호가 변경되었습니다.', 'success');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showToast(result.error?.message || '비밀번호 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('비밀번호 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: () => adminApi.updateMyEmail({ newEmail, password: emailPassword }),
    onSuccess: (result) => {
      if (result.success) {
        showToast('이메일이 변경되었습니다.', 'success');
        setNewEmail('');
        setEmailPassword('');
        refetchMe();
      } else {
        showToast(result.error?.message || '이메일 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('이메일 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const updateLoginIdMutation = useMutation({
    mutationFn: () => adminApi.updateMyLoginId({ loginId, password: loginIdPassword }),
    onSuccess: (result) => {
      if (result.success) {
        showToast('사용자 지정 ID가 변경되었습니다.', 'success');
        setLoginId('');
        setLoginIdPassword('');
        refetchMe();
      } else {
        showToast(result.error?.message || 'ID 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('ID 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  // 휴대폰 변경 - 인증코드 발송
  const requestPhoneChangeMutation = useMutation({
    mutationFn: (phone: string) => adminApi.requestPhoneChange(phone),
    onSuccess: (result) => {
      if (result.success) {
        setIsCodeSent(true);
        showToast('인증 코드가 발송되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '인증 코드 발송에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('인증 코드 발송 중 오류가 발생했습니다.', 'error');
    },
  });

  // 휴대폰 변경 - 인증코드 검증 및 완료
  const verifyPhoneChangeMutation = useMutation({
    mutationFn: ({ phone, code }: { phone: string; code: string }) =>
      adminApi.verifyPhoneChange(phone, code),
    onSuccess: (result) => {
      if (result.success) {
        showToast('휴대폰 번호가 변경되었습니다.', 'success');
        setIsPhoneChangeMode(false);
        setNewPhone('');
        setPhoneCode('');
        setIsCodeSent(false);
        refetchMe();
      } else {
        showToast(result.error?.message || '휴대폰 번호 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('휴대폰 번호 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast('이름을 입력해주세요.', 'error');
      return;
    }
    (document.activeElement as HTMLElement)?.blur();
    updateMutation.mutate({ name: name.trim() });
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      showToast('현재 비밀번호를 입력해주세요.', 'error');
      return;
    }
    if (!newPassword) {
      showToast('새 비밀번호를 입력해주세요.', 'error');
      return;
    }
    if (newPassword.length < 8) {
      showToast('새 비밀번호는 8자 이상이어야 합니다.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('새 비밀번호가 일치하지 않습니다.', 'error');
      return;
    }
    (document.activeElement as HTMLElement)?.blur();
    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) {
      showToast('새 이메일을 입력해주세요.', 'error');
      return;
    }
    if (!emailPassword) {
      showToast('현재 비밀번호를 입력해주세요.', 'error');
      return;
    }
    updateEmailMutation.mutate();
  };

  const handleLoginIdSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginId || loginId.length < 4) {
      showToast('ID는 4자 이상이어야 합니다.', 'error');
      return;
    }
    if (!loginIdPassword) {
      showToast('현재 비밀번호를 입력해주세요.', 'error');
      return;
    }
    updateLoginIdMutation.mutate();
  };

  const handleSendPhoneCode = (e: React.FormEvent) => {
    e.preventDefault();
    const phoneRegex = /^01[0-9]-\d{4}-\d{4}$/;
    if (!phoneRegex.test(newPhone)) {
      showToast('휴대폰 번호 형식이 올바르지 않습니다. (010-1234-5678)', 'error');
      return;
    }
    requestPhoneChangeMutation.mutate(newPhone);
  };

  const handleVerifyPhoneCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneCode.length !== 6) {
      showToast('인증 코드는 6자리입니다.', 'error');
      return;
    }
    verifyPhoneChangeMutation.mutate({ phone: newPhone, code: phoneCode });
  };

  const cancelPhoneChange = () => {
    setIsPhoneChangeMode(false);
    setNewPhone('');
    setPhoneCode('');
    setIsCodeSent(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">내 정보</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* 기본 정보 (이름) */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h3>

          <form onSubmit={handleNameSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이름
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={updateMutation.isPending || name === user?.name}
            >
              {updateMutation.isPending ? '저장 중...' : '이름 변경'}
            </Button>
          </form>
        </Card>

        {/* 휴대폰 번호 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">휴대폰 번호</h3>

          {!isPhoneChangeMode ? (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  현재 휴대폰 번호
                </label>
                <p className="text-gray-900">{phone || '-'}</p>
              </div>
              <Button variant="secondary" onClick={() => setIsPhoneChangeMode(true)}>
                휴대폰 번호 변경
              </Button>
            </div>
          ) : (
            <div>
              {!isCodeSent ? (
                <form onSubmit={handleSendPhoneCode} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      새 휴대폰 번호
                    </label>
                    <input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                        let formatted = digits;
                        if (digits.length > 3 && digits.length <= 7) {
                          formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                        } else if (digits.length > 7) {
                          formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
                        }
                        setNewPhone(formatted);
                      }}
                      placeholder="01012345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">숫자만 입력하면 자동 포맷됩니다</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={requestPhoneChangeMutation.isPending}
                    >
                      {requestPhoneChangeMutation.isPending ? '발송 중...' : '인증코드 발송'}
                    </Button>
                    <button
                      type="button"
                      onClick={cancelPhoneChange}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      취소
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyPhoneCode} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      새 휴대폰 번호
                    </label>
                    <p className="text-gray-900">{newPhone}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      인증 코드
                    </label>
                    <input
                      type="text"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6자리 숫자"
                      maxLength={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={verifyPhoneChangeMutation.isPending}
                    >
                      {verifyPhoneChangeMutation.isPending ? '변경 중...' : '번호 변경'}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setIsCodeSent(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      다시 입력
                    </button>
                    <button
                      type="button"
                      onClick={cancelPhoneChange}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      취소
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </Card>

        {/* 비밀번호 변경 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">비밀번호 변경</h3>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                현재 비밀번호
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <p className="mt-1 text-xs text-gray-500">8자 이상, 영문/숫자/특수문자 포함</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호 확인
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <Button type="submit" variant="primary" disabled={changePasswordMutation.isPending}>
              {changePasswordMutation.isPending ? '변경 중...' : '비밀번호 변경'}
            </Button>
          </form>
        </Card>

        {/* 이메일 변경 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">이메일 변경</h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-500 mb-1">
              현재 이메일
            </label>
            <p className="text-gray-900">{user?.email || '-'}</p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 이메일
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                현재 비밀번호 확인
              </label>
              <input
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <Button type="submit" variant="primary" disabled={updateEmailMutation.isPending}>
              {updateEmailMutation.isPending ? '변경 중...' : '이메일 변경'}
            </Button>
          </form>
        </Card>

        {/* 사용자 지정 ID 변경/추가 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">사용자 지정 ID</h3>
          <p className="text-xs text-gray-500 mb-4">
            이메일 대신 사용할 수 있는 로그인 ID입니다. (4~30자)
          </p>

          <form onSubmit={handleLoginIdSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 ID
              </label>
              <input
                type="text"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="my-admin-id"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                현재 비밀번호 확인
              </label>
              <input
                type="password"
                value={loginIdPassword}
                onChange={(e) => setLoginIdPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <Button type="submit" variant="primary" disabled={updateLoginIdMutation.isPending}>
              {updateLoginIdMutation.isPending ? '변경 중...' : 'ID 변경/추가'}
            </Button>
          </form>
        </Card>

        {/* 계정 정보 */}
        <Card padding="xl" radius="lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">계정 정보</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                계정 ID
              </label>
              <p className="text-gray-900 font-mono text-sm">{user?.id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                권한
              </label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                슈퍼관리자
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
